/* eslint-disable max-lines */
import { KlueUIProvider } from '@kluein/klue-ui';
import { getPersistor } from '@rematch/persist';
import {
  lazy,
  Suspense,
  useEffect,
  type ReactNode,
  type PropsWithChildren,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  Routes,
  useLocation,
  Route,
  Navigate,
  Outlet,
  matchPath,
} from 'react-router-dom';
// Child dependency of @rematch/persist
import { PersistGate } from 'redux-persist/integration/react';

import 'requestidlecallback-polyfill';
const CardEditorModalContainer = lazy(
  () => import('components/common/card-editor/CardEditorModalContainer'),
);
import { CardViewer } from 'components/common/card-viewer';
import { PageNotAuthorized } from 'components/fallback';
import PageNotFound from 'components/fallback/PageNotFound';
import { MainLayout, MainLayoutContent } from 'components/layout/main-layout';
import { useAuth } from 'contexts/auth';
import { AppUIProvider } from 'contexts/ui';
import { useBulkEdit } from 'contexts/ui/bulk-edit-context';
import { ImpersonationGuardNotifier } from 'contexts/ui/ImpersonationGuardNotifier';
import { RecoveryRequestsNotifier } from 'contexts/ui/RecoveryRequestsNotifier';
import { SessionTimeoutNotifier } from 'contexts/ui/SessionTimeoutNotifier';
import { useFlashMessage } from 'hooks/use-flash-message';
import { useRedirect } from 'hooks/use-redirect';
import Embeds, { HatchEmbeds } from 'lib/Embeds';
import ErrorBoundary from 'lib/ErrorBoundary';
import PendoResourceTrigger from 'lib/pendo/PendoResourceTrigger';
import { COMPETE_EVAL_URL_BASE } from 'modules/compete-eval/route-paths';
import { RecoveryRequestsNotifier as HatchRecoveryRequestsNotifier } from 'modules/hatch/components/recovery-requests-notifier/RecoveryRequestsNotifier';
import { SessionChangedNotifier } from 'modules/hatch/components/session-changed-notifier/SessionChangedNotifier';
import { SessionTimeoutNotifier as HatchSessionTimeoutNotifier } from 'modules/hatch/components/session-timeout-notifier/SessionTimeoutNotifier';
import { SFDC_LWC_WIDGET_ROUTE } from 'modules/hatch/pages/sfdc-widget-page/utils/widget.constants';
import { HATCH_URL_BASE } from 'modules/hatch/route-paths';
import {
  WIN_LOSS_URL_BASE,
  WIN_LOSS_PUBLIC_URL_BASE,
} from 'modules/win-loss/constants';
import { useAskKlue } from 'pages/dashboard/hooks';
import { AskKluePage, AskKlueEmbed } from 'pages/dashboard/partials/ask-klue';
import { AskKlueModal } from 'pages/dashboard/partials/ask-klue/partials/ask-klue-modal';

import type { Dispatch } from 'store/store.types';

// Use Lazy load to split routes into chunks and reduce the bundle load time
const ProfileView = lazy(() => import('./pages/profile/Router'));
const ReportsView = lazy(() => import('./pages/reports/ReportsView'));
const AlertsRouter = lazy(() => import('./pages/alerts/router'));
const SearchView = lazy(() => import('./pages/search/SearchView'));
const IntegrationsRouter = lazy(() => import('./pages/integrations/Router'));

const DashboardView = lazy(() => import('./pages/dashboard/DashboardView'));
const ReviewInsightsRouter = lazy(
  () => import('./pages/review-insights/router'),
);
const MonitorsRouter = lazy(() => import('./pages/monitors/router'));
const BattlecardPrintView = lazy(
  () => import('./pages/battlecard-print/BattlecardPrintView'),
);
const CardEmbedsView = lazy(() => import('./pages/embed-card/CardEmbedsView'));
const CardRedirector = lazy(() => import('./pages/card-redirector'));

const PublicWinLossRouter = lazy(
  () => import('./modules/win-loss/public-router'),
);
const WinLossRouter = lazy(() => import('./modules/win-loss/router'));
const HatchRouter = lazy(() => import('./modules/hatch/router'));
const WinLossPromoRouter = lazy(
  () => import('./modules/win-loss/promo-router'),
);

const SfdcWidgetEmbed = lazy(() =>
  import('./modules/hatch/pages/sfdc-widget-page/SfdcWidgetEmbed').then(
    (m) => ({ default: m.SfdcWidgetEmbed }),
  ),
);

const SfdcWidgetLwcBridge = lazy(() =>
  import('./modules/hatch/pages/sfdc-widget-page/SfdcWidgetLwcBridge').then(
    (m) => ({ default: m.SfdcWidgetLwcBridge }),
  ),
);

const SettingsRouter = lazy(() => import('./pages/settings/router'));

const CallInsightsRouter = lazy(() => import('./pages/call-insights/Router'));

const ExtensionGuideView = lazy(
  () => import('./pages/extension/ExtensionGuideView'),
);

const CompanyRouter = lazy(() => import('./pages/company/CompanyRouter'));

const KnowledgeHubRouter = lazy(() => import('./pages/knowledge-hub/'));

const CompeteEvalRouter = lazy(() => import('./modules/compete-eval/router'));

const DealTipsPage = lazy(() => import('./pages/deal-tips/DealTipsPage'));
const DealTipDetailPage = lazy(
  () => import('./pages/deal-tips/DealTipDetailPage'),
);

// For persistent redux state
const persistor = getPersistor();

function RedirectToV1() {
  const { redirectToV1 } = useRedirect();
  redirectToV1();
  return null;
}

// Mounts the session warnings once per page: the cross-tab session change, the session timeout,
// and pending account-recovery requests for admins. Current Compete gets the klue-ui versions
// and Compete 2.0 the Mantine ones. Routes below nest their own AppUIProvider, so the warnings
// live here rather than inside AppUIProvider, which would show them once per nesting.
function AuthDependentProviders({ children }: PropsWithChildren) {
  const { isHatchExclusive } = useAuth();

  if (!isHatchExclusive) {
    return (
      <KlueUIProvider>
        <AppUIProvider>
          <ImpersonationGuardNotifier />
          <SessionTimeoutNotifier />
          <RecoveryRequestsNotifier />
          {children}
        </AppUIProvider>
      </KlueUIProvider>
    );
  }

  return (
    <>
      <SessionChangedNotifier />
      <HatchSessionTimeoutNotifier />
      <HatchRecoveryRequestsNotifier />
      {children}
    </>
  );
}

/**
 * The application main container
 * @component
 *
 * @visibleName App
 */

function App() {
  const dispatch = useDispatch<Dispatch>();
  const { showModal } = useAskKlue();

  const {
    homeRoute,
    isReportsEnabled,
    isCurator,
    isAdmin,
    isHatchAdmin,
    user,
    isWinLossEnabled,
    isHatchExclusive,
    isNewCardEditorEnabled,
    isKnowledgeHubEnabled,
    isUnifiedSearchEnabled,
    isAskKlueDashboardPageEnabled,
    isCallInsightsDisabled,
    isDealTipsEnabled,
  } = useAuth();
  const { isActive: isBulkEditActive } = useBulkEdit();
  const { redirectToV1, redirectToV1Admin } = useRedirect();
  const { pathname, search } = useLocation();

  // Display flash messages from backend (e.g., after redirect from v1)
  useFlashMessage(user);

  useEffect(() => {
    // it helps reducing the priority for this call
    // TODO: Implement a better strategy to fetch rivals
    // without blocking the initial load for most important content
    window.requestIdleCallback(function () {
      dispatch.rivals.fetchAllRivals(null);
    });
  }, [dispatch]);

  // Handle deal tip redirect for Hatch exclusive users
  if (isHatchExclusive && search) {
    const urlParams = new URLSearchParams(search);
    const askKlueEnricherId = urlParams.get('askKlueEnricherId');

    if (askKlueEnricherId && askKlueEnricherId.trim() !== '') {
      // Use window.location.href to bypass React Router interference with previewing parameter
      window.location.href = `${HATCH_URL_BASE}/deals/${encodeURIComponent(
        askKlueEnricherId,
      )}`;
      return null;
    }
  }

  if (pathname.startsWith('/admin')) {
    redirectToV1Admin();

    return null;
  }

  // Reports don't exist in v1, render not authorized page instead
  if (pathname.startsWith('/reports') && !isReportsEnabled) {
    return <PageNotAuthorized />;
  }

  // Handle Hatch exclusive access control
  if (isHatchExclusive) {
    // If user has Hatch exclusive access but is not on /next routes, redirect to /next
    // Exception: Allow /integrations for HatchAdmin users, and /win-loss for
    // anyone who actually has 1.0 Win-Loss (plus HatchAdmins, who land on the
    // promo router when they don't). This is where the Workspace sidebar's
    // Win-Loss rows point: the only ones for accounts still on 1.0
    // (isWinLossV2Enabled off), and the "Win-Loss Classic" row for accounts
    // on 2.0. 1.0 stays reachable for as long as the account has it, so the
    // exception has to outlive the 2.0 module rollout.
    if (
      !pathname.startsWith(HATCH_URL_BASE) &&
      !pathname.startsWith('/sfdc-embed/') &&
      !pathname.startsWith(SFDC_LWC_WIDGET_ROUTE) &&
      !pathname.startsWith(COMPETE_EVAL_URL_BASE) &&
      !(isHatchAdmin && pathname.startsWith('/integrations')) &&
      !(
        (isHatchAdmin || isWinLossEnabled) &&
        pathname.startsWith(WIN_LOSS_URL_BASE)
      )
    ) {
      return <Navigate to={HATCH_URL_BASE} replace />;
    }
  } else {
    // If user doesn't have Hatch exclusive access but is trying to access /next routes, redirect to /dashboard
    if (pathname.startsWith(HATCH_URL_BASE)) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  if (isCurator) {
    if (
      pathname.startsWith('/profile') &&
      !isBulkEditActive &&
      !isNewCardEditorEnabled
    ) {
      redirectToV1();
      return null;
    }
  }

  // Alerts can be accessed only by curators
  if (pathname.startsWith('/alerts') && !isCurator) {
    return <Navigate to="/dashboard" replace />;
  }

  // Research module and Compete Composer have been sunsetted
  if (pathname.startsWith('/research') || pathname.startsWith('/composer')) {
    return <Navigate to="/dashboard" replace />;
  }

  const hasAccessToInsight = isAdmin || isCurator || user?.isImpersonating;

  if (pathname.startsWith('/insights') && !hasAccessToInsight) {
    return <Navigate to="/dashboard" replace />;
  }

  if (pathname.startsWith('/review-insights')) {
    return <Navigate to="/insights" replace />;
  }

  // TODO (rhuan): this will be removed later, given that we will bring this page to v2
  if (pathname.startsWith('/winloss/survey')) {
    redirectToV1();
    return null;
  }

  if (
    !!matchPath(
      { path: WIN_LOSS_PUBLIC_URL_BASE, caseSensitive: true, end: false },
      pathname,
    )
  ) {
    return (
      <Routes>
        <Route
          path={`${WIN_LOSS_URL_BASE}/*`}
          element={<PublicWinLossRouter />}
        />
      </Routes>
    );
  }

  // Render Hatch-specific layout when isHatchExclusive is true
  // This bypasses the MainLayout and renders only Hatch routes
  if (isHatchExclusive) {
    return (
      <>
        <PersistGate persistor={persistor}>
          {() => (
            <Suspense fallback={<div />}>
              <ErrorBoundary>
                <AuthDependentProviders>
                  <HatchEmbeds />
                  <Routes>
                    <Route
                      path={`${COMPETE_EVAL_URL_BASE}/*`}
                      element={
                        <KlueUIProvider>
                          <AppUIProvider>
                            <CompeteEvalRouter />
                          </AppUIProvider>
                        </KlueUIProvider>
                      }
                    />
                    <Route
                      path={`${HATCH_URL_BASE}/*`}
                      element={<HatchRouter />}
                    />
                    {(isHatchAdmin || isWinLossEnabled) && (
                      <Route
                        path={`${WIN_LOSS_URL_BASE}/*`}
                        element={
                          <KlueUIProvider>
                            <AppUIProvider>
                              <MainLayoutContent>
                                {isWinLossEnabled ? (
                                  <WinLossRouter />
                                ) : (
                                  <WinLossPromoRouter />
                                )}
                              </MainLayoutContent>
                            </AppUIProvider>
                          </KlueUIProvider>
                        }
                      />
                    )}
                    <Route
                      path="/sfdc-embed/deal-tips"
                      element={<SfdcWidgetEmbed />}
                    />
                    <Route
                      path={SFDC_LWC_WIDGET_ROUTE}
                      element={<SfdcWidgetLwcBridge />}
                    />
                    <Route path="*" element={<PageNotFound />} />
                  </Routes>
                </AuthDependentProviders>
              </ErrorBoundary>
            </Suspense>
          )}
        </PersistGate>
      </>
    );
  }

  return (
    <>
      <PersistGate persistor={persistor}>
        {() => (
          <Suspense fallback={<div />}>
            <ErrorBoundary>
              <AuthDependentProviders>
                <Embeds />
                <PendoResourceTrigger />
                <Routes>
                  <Route
                    path="/"
                    element={
                      <MainLayout>
                        <CardViewer />
                        <CardEditorModalContainer />
                        {showModal && isUnifiedSearchEnabled && (
                          <AskKlueModal />
                        )}
                        <Outlet />
                      </MainLayout>
                    }
                  >
                    <Route
                      path="/"
                      element={
                        isAskKlueDashboardPageEnabled ? (
                          <Navigate to="/ask-klue" replace />
                        ) : (
                          <Navigate to={homeRoute} replace />
                        )
                      }
                    />
                    <Route path="/ask-klue" element={<AskKluePage />} />
                    <Route path="/dashboard" element={<DashboardView />} />
                    <Route path="alerts/*" element={<AlertsRouter />} />
                    {isKnowledgeHubEnabled && (
                      <Route
                        path="/knowledge-hub/*"
                        element={<KnowledgeHubRouter />}
                      />
                    )}
                    <Route path="/company/*" element={<CompanyRouter />} />
                    <Route path="/search/*" element={<SearchView />} />
                    <Route path="/profile/:id/*" element={<ProfileView />} />
                    <Route
                      path="/extension/guide/*"
                      element={<ExtensionGuideView />}
                    />
                    <Route
                      path="/integrations/*"
                      element={<IntegrationsRouter />}
                    />
                    <Route
                      path="/insights/*"
                      element={<ReviewInsightsRouter />}
                    />
                    {isCurator && !isCallInsightsDisabled && (
                      <Route
                        path="/call-insights/*"
                        element={<CallInsightsRouter />}
                      />
                    )}
                    <Route path="/monitors/*" element={<MonitorsRouter />} />
                    {isAdmin && isDealTipsEnabled && (
                      <>
                        <Route path="/deal-tips" element={<DealTipsPage />} />
                        <Route
                          path="/deal-tips/:id"
                          element={<DealTipDetailPage />}
                        />
                      </>
                    )}
                    <Route
                      path={`${HATCH_URL_BASE}/*`}
                      element={<HatchRouter />}
                    />
                    <Route
                      path={`${WIN_LOSS_URL_BASE}/*`}
                      element={
                        isWinLossEnabled ? (
                          <WinLossRouter />
                        ) : (
                          <WinLossPromoRouter />
                        )
                      }
                    />
                    <Route path="/settings/*" element={<SettingsRouter />} />
                    <Route path="/card/:cardId" element={<CardRedirector />} />
                    <Route path="*" element={<PageNotFound />} />
                    {reportsRoute(<ReportsView />)}
                  </Route>
                  <Route
                    path={`${COMPETE_EVAL_URL_BASE}/*`}
                    element={
                      <KlueUIProvider>
                        <AppUIProvider>
                          <CompeteEvalRouter />
                        </AppUIProvider>
                      </KlueUIProvider>
                    }
                  />
                  <Route
                    path="/profile/:id/battlecard/:battlecardId/print"
                    element={<BattlecardPrintView />}
                  />
                  <Route
                    path="/card/embed/:cardId"
                    element={<CardEmbedsView />}
                  />
                  <Route path="/ask-klue/embed" element={<AskKlueEmbed />} />
                  <Route
                    path="/sfdc-embed/deal-tips"
                    element={<SfdcWidgetEmbed />}
                  />
                  <Route
                    path={SFDC_LWC_WIDGET_ROUTE}
                    element={<SfdcWidgetLwcBridge />}
                  />
                  <Route path="/feed" element={<RedirectToV1 />} />
                </Routes>
              </AuthDependentProviders>
            </ErrorBoundary>
          </Suspense>
        )}
      </PersistGate>
    </>
  );
}

export const reportsRoute = (reportsView: ReactNode) => (
  <Route path="/reports/*" element={reportsView}>
    <Route path=":id" element={null} />
    <Route path=":id/:type" element={null} />
    <Route path=":id/:type/user/:modeUser" element={null} />
    <Route path=":id/:type/:drillDownReport" element={null} />
    <Route
      path="not-authorized"
      element={<PageNotAuthorized fullscreen={false} />}
    />
  </Route>
);

export default App;
